/**
 * ReportUpdatePage.jsx — v1.0.0 Gate 5.5 Item 1
 *
 * Lightweight form for reporting business updates/corrections.
 * Picks a business (searchable), picks an update type, adds an optional note.
 * POSTs to /api/contributions/report-update → awards 10 pts via update_reported rule.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Flag, Search } from "lucide-react";
import { API, axios } from "@/api";
import { useTheme } from "@/contexts/ThemeContext";
import { BottomNav } from "@/components/shared/Navigation";

const UPDATE_TYPES = [
  { id: "hours_changed",   label: "Hours changed" },
  { id: "offer_changed",   label: "Birthday offer changed" },
  { id: "menu_changed",    label: "Menu changed" },
  { id: "address_changed", label: "Address changed" },
  { id: "phone_changed",   label: "Phone changed" },
  { id: "closed_down",     label: "Closed down" },
  { id: "other",           label: "Other" },
];

export default function ReportUpdatePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [updateType, setUpdateType] = useState("hours_changed");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/birthday-offers?search=${encodeURIComponent(query)}`);
        setResults((res.data?.offers || res.data || []).slice(0, 10));
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const handleSubmit = async () => {
    if (!selected && !note.trim()) {
      toast.error("Pick a business or add a note");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contributions/report-update`, {
        business_id: selected?.business_id,
        update_type: updateType,
        note,
      });
      toast.success("Update report submitted — thanks!");
      setTimeout(() => navigate("/activity"), 1200);
    } catch {
      toast.error("Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: theme.pageBg }} data-testid="report-update-page">
      <div className="px-5 pt-6 pb-4" style={{ background: theme.headerGradient }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-xl bg-white/10" data-testid="report-update-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white" data-testid="report-update-title">Report an Update</h1>
            <p className="text-white/70 text-xs mt-0.5">+10 points. Help us keep it accurate.</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Search business */}
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">Which business?</p>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search business name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
              data-testid="report-update-search"
            />
          </div>
          {(results.length > 0 || searching) && (
            <div className="mt-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="report-update-results">
              {searching && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>}
              {results.map((r) => {
                const active = selected?.business_id === r.business_id;
                return (
                  <button
                    key={r.business_id}
                    onClick={() => setSelected(r)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b border-slate-50 last:border-0 ${
                      active ? "bg-emerald-50" : "hover:bg-slate-50"
                    }`}
                    data-testid={`report-update-result-${r.business_id}`}
                  >
                    <Flag className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-800 truncate">{r.name}</span>
                    {active && <span className="ml-auto text-[10px] font-bold text-emerald-600">Selected</span>}
                  </button>
                );
              })}
            </div>
          )}
          {selected && !searching && (
            <p className="text-[10px] text-emerald-600 mt-1">Selected: {selected.name}</p>
          )}
        </div>

        {/* Update type */}
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">What changed?</p>
          <div className="flex flex-wrap gap-1.5" data-testid="report-update-types">
            {UPDATE_TYPES.map((t) => {
              const active = updateType === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setUpdateType(t.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
                    active
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : "bg-white text-slate-600 border-slate-200"
                  }`}
                  data-testid={`report-update-type-${t.id}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note */}
        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2">Details (optional)</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 400))}
            placeholder="Anything else we should know?"
            rows={3}
            maxLength={400}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200"
            data-testid="report-update-note"
          />
          <p className="text-[10px] text-slate-400 mt-1 text-right">{note.length}/400</p>
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || (!selected && !note.trim())}
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold disabled:opacity-50"
          data-testid="report-update-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Submit (+10 pts)"}
        </button>
      </div>

      <BottomNav />
    </div>
  );
}
